import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 2,
  "data-test": "login-form-wrapper",
  class: "tw-w-full tw-border tw-border-core-neutral-cold-90 tw-rounded-2xl"
}
const _hoisted_2 = { class: "headlines--headline-05 tw-text-core-primary-10 tw-px-6 tw-py-4 tw-m-0 tw-border-b tw-border-core-neutral-cold-90" }
const _hoisted_3 = { class: "headlines--headline-03 tw-text-core-primary-10 tw-text-center" }
const _hoisted_4 = {
  key: 1,
  class: "tw-flex tw-flex-col tw-gap-4 tw-p-4 md:tw-p-6 tw-items-center tw-justify-center"
}
const _hoisted_5 = {
  "data-test": "sso-section-title",
  class: "headlines--headline-03 tw-text-core-primary-10 tw-text-center"
}
const _hoisted_6 = { class: "tw-w-full tw-py-4 tw-px-6 tw-rounded-2xl tw-bg-core-primary-99" }
const _hoisted_7 = { class: "tw-flex tw-items-center tw-gap-3" }
const _hoisted_8 = { class: "headlines--headline-04 tw-text-core-primary-10 tw-mb-1" }

import AuthorizationCredentials from 'frontcore/domain/authorization/view/AuthorizationCredentials.vue'
import AuthorizationSAML from 'frontcore/domain/authorization/view/AuthorizationSAML.vue'
import AuthorizationSSO from 'frontcore/domain/authorization/view/AuthorizationSSO.vue'
import CoreAlert from 'frontcore/presentation/alert/views/CoreAlert.vue'
import CoreLink from 'frontcore/views/link/CoreLink.vue'
import i18n from 'frontcore/i18n'
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import useLoginConfigurationStore from 'frontcore/domain/authorization/store/useLoginConfigurationStore'
import { AlertStatus } from 'frontcore/presentation/alert/domain/types'
import { computed, ref } from 'vue'
import { FactoryAllowedLoginType } from 'frontcore/domain/authorization/model/types'
import { useRoute } from 'vue-router'

type Props = {
  applicationId?: string,
  applicationName: string,
  isFetchingUserConfiguration: boolean,
  isLoginSupport?: boolean
  allowedLoginType?: FactoryAllowedLoginType
}

type Emits = (e: 'loginSuccess') => void

const __default__ = { name: 'MLLogin' }

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
    applicationId: {},
    applicationName: {},
    isFetchingUserConfiguration: { type: Boolean },
    isLoginSupport: { type: Boolean, default: false },
    allowedLoginType: { default: FactoryAllowedLoginType.PASSWORD }
  },
  emits: ["loginSuccess"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { t } = i18n.global
const route = useRoute()

const { isSAMLLogin, isSSOLogin, ssoConfigurationValue } = useLoginConfigurationStore()

const hasCorporateLogin = computed(() => {
  const hasSSOLoginType = [
    FactoryAllowedLoginType.SSO,
    FactoryAllowedLoginType.PASSWORD_AND_SSO,
  ].includes(props.allowedLoginType)
  return (isSAMLLogin || isSSOLogin) && !props.isLoginSupport && hasSSOLoginType
})
const logoSource = `${API_URL}/media/files/branding/logo-login.png`

const loginSuccessHandler = () => {
  emit('loginSuccess')
}
const shouldShowSSOLogin = computed(() =>
  isSSOLogin && ssoConfigurationValue && !props.isLoginSupport
)
const shouldShowSAMLLogin = computed(() =>
  isSAMLLogin && !props.isLoginSupport
)
const shouldShowLoginConfigError = computed(() => {
  return [
    FactoryAllowedLoginType.SSO,
    FactoryAllowedLoginType.PASSWORD_AND_SSO,
  ].includes(props.allowedLoginType)
    && !isSSOLogin && !isSAMLLogin && !props.isLoginSupport
})
const errorMessage = ref<string | null>(null)
const paramErrorMessage = ref<string | null>(null)

if (route.query.error) {
  const paramErrorToken = typeof(route.query.error) === 'string' ? route.query.error : ''
  paramErrorMessage.value = t(paramErrorToken)
}

const errorHandler = (message: string | null) => {
  if (message === null) {
    errorMessage.value = message
  } else {
    errorMessage.value = t(message)
  }
}

const hasPasswordAndSSOType = computed(() => {
  return props.allowedLoginType === FactoryAllowedLoginType.PASSWORD_AND_SSO && hasCorporateLogin.value
})

const hasPasswordType = computed(() => {
  return [
    FactoryAllowedLoginType.PASSWORD_AND_SSO,
    FactoryAllowedLoginType.PASSWORD,
  ].includes(props.allowedLoginType)
})

const formColClasses = computed(() => {
  if (hasPasswordAndSSOType.value) {
    return 'tw-grid-cols-1 md:tw-grid-cols-2'
  }
  return 'tw-grid-cols-1'
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["tw-mx-auto tw-h-screen tw-w-72 tw-pt-10 sm:tw-pt-0 tw-flex tw-flex-col tw-gap-6 tw-items-center tw-justify-start sm:tw-justify-center", [hasPasswordAndSSOType.value ? 'sm:tw-w-[32rem]' : 'sm:tw-w-96']])
  }, [
    _createElementVNode("img", {
      src: logoSource,
      alt: "logo",
      class: "tw-h-12"
    }),
    (paramErrorMessage.value)
      ? (_openBlock(), _createBlock(CoreAlert, {
          key: 0,
          class: "tw-w-full",
          "data-test": "param-error-alert-message",
          message: paramErrorMessage.value,
          status: _unref(AlertStatus).ERROR
        }, null, 8, ["message", "status"]))
      : _createCommentVNode("", true),
    (shouldShowLoginConfigError.value)
      ? (_openBlock(), _createBlock(CoreAlert, {
          key: 1,
          class: "tw-w-full",
          "data-test": "login-configuration-error-alert-message",
          message: _unref(t)('login.errors.login_type_configuration'),
          status: _unref(AlertStatus).ERROR
        }, null, 8, ["message", "status"]))
      : (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)('common.messages.login_title', { solution: _ctx.applicationName })), 1),
          _createElementVNode("div", {
            class: _normalizeClass(["tw-grid", formColClasses.value])
          }, [
            (hasPasswordType.value || _ctx.isLoginSupport)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["tw-flex tw-flex-col tw-p-6 tw-gap-4", {
            'tw-border-b md:tw-border-b-0 md:tw-border-r tw-border-core-neutral-cold-90': hasPasswordAndSSOType.value
          }])
                }, [
                  _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('login.messages.login_with_username_password')), 1),
                  _createVNode(AuthorizationCredentials, {
                    applicationId: _ctx.applicationId,
                    isFetchingUserConfiguration: _ctx.isFetchingUserConfiguration,
                    onError: errorHandler,
                    onLoginSuccess: loginSuccessHandler
                  }, null, 8, ["applicationId", "isFetchingUserConfiguration"])
                ], 2))
              : _createCommentVNode("", true),
            (hasCorporateLogin.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                  _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('login.messages.signin_with')), 1),
                  (shouldShowSSOLogin.value)
                    ? (_openBlock(), _createBlock(AuthorizationSSO, {
                        key: 0,
                        applicationId: _ctx.applicationId,
                        configuration: _unref(ssoConfigurationValue),
                        isFetchingUserConfiguration: _ctx.isFetchingUserConfiguration,
                        onError: errorHandler,
                        onLoginSuccess: loginSuccessHandler
                      }, null, 8, ["applicationId", "configuration", "isFetchingUserConfiguration"]))
                    : _createCommentVNode("", true),
                  (shouldShowSAMLLogin.value)
                    ? (_openBlock(), _createBlock(AuthorizationSAML, {
                        key: 1,
                        applicationId: _ctx.applicationId,
                        isFetchingUserConfiguration: _ctx.isFetchingUserConfiguration,
                        onError: errorHandler,
                        onLoginSuccess: loginSuccessHandler
                      }, null, 8, ["applicationId", "isFetchingUserConfiguration"]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ], 2)
        ])),
    (errorMessage.value)
      ? (_openBlock(), _createBlock(CoreAlert, {
          key: 3,
          class: "tw-w-full",
          message: errorMessage.value,
          status: _unref(AlertStatus).ERROR
        }, null, 8, ["message", "status"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createVNode(SVGIcon, {
          colorClass: "tw-fill-core-primary-50",
          iconName: "MenuBook"
        }),
        _createElementVNode("div", null, [
          _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)('common.messages.need_help')), 1),
          _createVNode(CoreLink, {
            linkText: _unref(t)('common.messages.user_guide'),
            dataTest: "user-guide-link",
            isExternal: "",
            to: "https://doc.mlean.com"
          }, null, 8, ["linkText"])
        ])
      ])
    ])
  ], 2))
}
}

})